import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { CircleHelp, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Panel } from "@/components/admin/Fields";
import { adminResolveUncertain, errorMessage } from "@/lib/api";
import { fmtDateTime } from "@/lib/tickets";

const REASON = { stale_lock: "Køyringa stoppa midt i sendinga", send_error: "Leverandøren gav ikkje klart svar" };

/** Messages whose provider outcome is unknown. They are never retried automatically – the board asks the recipient and answers here. */
export const UncertainSends = ({ rows }) => {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(null);
  const resolve = useMutation({
    mutationFn: ({ id, action }) => adminResolveUncertain(id, action),
    onMutate: ({ id, action }) => setBusy(`${id}:${action}`),
    onSettled: () => setBusy(null),
    onSuccess: (r, { action }) => {
      qc.invalidateQueries({ queryKey: ["admin-integrations"] });
      if (action === "received") toast.success("Registrert som motteken – ingenting sendt");
      else if (action === "drop") toast.success("Stroken – ingenting sendt");
      else toast.success(r.status === "sent" ? "Sendt på nytt – éin gong" : r.status === "queued" ? "Nattestopp: går kl. 08:00" : `Ny sending: ${r.reason || r.error || r.status}`);
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  if (!rows?.length) return null;
  return (
    <Panel title={`Usikre sendingar (${rows.length})`} className="mt-6" testId="uncertain-sends-panel"
      description="Leverandøren svara ikkje klart (tidsavbrot, brote samband eller køyringa stoppa midt i). Systemet sender ALDRI desse om att på eiga hand – spør mottakaren, og svar her. «Ikkje motteke» sender éin gong til; «Motteke» lukkar utan å sende.">
      <ul className="divide-y divide-border" data-testid="uncertain-sends-list">
        {rows.map((r) => (
          <li key={r.id} className="flex flex-col gap-3 py-3 sm:flex-row sm:items-center sm:justify-between" data-testid={`uncertain-${r.id}`}>
            <div className="min-w-0 text-[13px]">
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                <CircleHelp className="h-4 w-4 shrink-0 text-status-warning" />
                <span className="font-medium">{r.label}</span>
                {r.ref && <span className="font-mono text-[12px] text-muted-foreground">{r.ref}</span>}
                {r.event && <span className="text-muted-foreground">· {r.event}</span>}
              </div>
              <div className="mt-1 text-[12px] text-muted-foreground">
                {r.to ? <>Til {r.to} · </> : null}{REASON[r.reason] || r.reason}{r.error ? ` – ${r.error}` : ""} · {fmtDateTime(r.at)}
              </div>
            </div>
            <div className="flex shrink-0 flex-wrap gap-2">
              <Button type="button" size="sm" variant="outline" className="h-8 bg-card" disabled={!!busy} onClick={() => resolve.mutate({ id: r.id, action: "received" })} data-testid={`uncertain-received-${r.id}`}>{busy === `${r.id}:received` ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null} Motteke</Button>
              <Button type="button" size="sm" variant="outline" className="h-8 bg-card" disabled={!!busy} onClick={() => resolve.mutate({ id: r.id, action: "not_received" })} data-testid={`uncertain-resend-${r.id}`}>{busy === `${r.id}:not_received` ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null} Ikkje motteke – send éin gong til</Button>
              <Button type="button" size="sm" variant="ghost" className="h-8" disabled={!!busy} onClick={() => resolve.mutate({ id: r.id, action: "drop" })} data-testid={`uncertain-drop-${r.id}`}>Stryk</Button>
            </div>
          </li>
        ))}
      </ul>
    </Panel>
  );
};
